// Reads and writes the theme preference in localStorage. This is the only
// place that knows how preferences are persisted, so moving them to the user
// profile later means changing this module and nothing else.
//
// Stored shape: { "version": 1, "appearance": "dark", "accentColor": "violet" }
// public/index.html reads the same key before the app loads to avoid a flash
// of the wrong theme; keep the two in sync.

import {
  DEFAULT_THEME_PREFERENCES,
  normalizeThemePreferences,
} from "./themeConfig";

export const THEME_STORAGE_KEY = "app-theme-preferences";
export const THEME_STORAGE_VERSION = 1;

// localStorage can be missing (SSR, tests) or throw on access (blocked
// storage, some private modes).
function getStorage() {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

function serialize(preferences) {
  return JSON.stringify({
    version: THEME_STORAGE_VERSION,
    appearance: preferences.appearance,
    accentColor: preferences.accentColor,
  });
}

export function readThemePreferences() {
  const storage = getStorage();
  if (!storage) return { ...DEFAULT_THEME_PREFERENCES };
  try {
    const raw = storage.getItem(THEME_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_THEME_PREFERENCES };
    const parsed = JSON.parse(raw);
    // Only version 1 exists. A later format would be migrated here.
    if (!parsed || parsed.version !== THEME_STORAGE_VERSION) {
      return { ...DEFAULT_THEME_PREFERENCES };
    }
    return normalizeThemePreferences(parsed);
  } catch {
    return { ...DEFAULT_THEME_PREFERENCES };
  }
}

// Returns false when the preference could not be saved; the theme still
// applies for the current page.
export function writeThemePreferences(preferences) {
  const storage = getStorage();
  if (!storage) return false;
  try {
    const next = serialize(normalizeThemePreferences(preferences));
    if (storage.getItem(THEME_STORAGE_KEY) !== next) {
      storage.setItem(THEME_STORAGE_KEY, next);
    }
    return true;
  } catch {
    return false;
  }
}
